import React, { useState, useEffect, useCallback } from 'react';
import { servicePickupsApi, staffApi } from '../api/client';
import { Modal, Spin, Empty, useFlash, F, Confirm, parseErr } from '../components/UI';
import { useAuth } from '../context/AuthContext';
import { Plus, Edit, Trash, Wrench } from '../components/Icons';

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const fmtDate = d => d ? new Date(d+'T00:00:00').toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';

const STATUS_STYLE = {
  with_us:    { label: 'With Us',             bg: '#dbeafe', color: '#1d4ed8' },
  at_service: { label: 'At Service Center',   bg: '#fef3c7', color: '#b45309' },
  returned:   { label: 'Returned to Customer',bg: '#d1fae5', color: '#065f46' },
};

const blankForm = () => ({
  customer_name: '', customer_address: '', product_name: '', serial_number: '',
  taken_date: todayStr(), sent_to_service_date: '', returned_date: '',
  returned_to_customer_date: '', taken_by_staff_id: '', status: 'with_us', notes: '',
});

export default function ServicePickups() {
  const { canWrite, canDelete } = useAuth();
  const { show, Flash } = useFlash();
  const [rows,    setRows]    = useState([]);
  const [staff,   setStaff]   = useState([]);
  const [busy,    setBusy]    = useState(true);
  const [showModal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [delItem, setDelItem] = useState(null);
  const [form,    setForm]    = useState(blankForm());
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState('');
  const [filter,  setFilter]  = useState('all');
  const [q,       setQ]       = useState('');

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const setDate = k => e => {
    const val = e.target.value;
    setForm(f => {
      const next = { ...f, [k]: val };
      if (k === 'sent_to_service_date'      && val) next.status = 'at_service';
      if (k === 'returned_date'             && val) next.status = 'with_us';
      if (k === 'returned_to_customer_date' && val) next.status = 'returned';
      return next;
    });
  };

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [{ data: pickups }, { data: st }] = await Promise.all([
        servicePickupsApi.list({ page_size: 500 }),
        staffApi.list(),
      ]);
      setRows(pickups);
      setStaff(st.filter(s => s.is_active && s.role !== 'superadmin'));
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null); setForm(blankForm()); setErr(''); setModal(true);
  };

  const openEdit = row => {
    setEditing(row);
    setForm({
      customer_name:       row.customer_name || '',
      customer_address:    row.customer_address || '',
      product_name:        row.product_name || '',
      serial_number:       row.serial_number || '',
      taken_date:          row.taken_date || todayStr(),
      sent_to_service_date:      row.sent_to_service_date || '',
      returned_date:             row.returned_date || '',
      returned_to_customer_date: row.returned_to_customer_date || '',
      taken_by_staff_id:         row.taken_by_staff_id || '',
      status:              row.status || 'with_us',
      notes:               row.notes || '',
    });
    setErr(''); setModal(true);
  };

  const save = async () => {
    if (!form.customer_name.trim()) { setErr('Customer name is required.'); return; }
    if (!form.product_name.trim())  { setErr('Product name is required.'); return; }
    if (!form.taken_date)           { setErr('Taken date is required.'); return; }
    setSaving(true); setErr('');
    const payload = {
      customer_name:       form.customer_name.trim(),
      customer_address:    form.customer_address.trim() || null,
      product_name:        form.product_name.trim(),
      serial_number:       form.serial_number.trim() || null,
      taken_date:          form.taken_date,
      sent_to_service_date:      form.sent_to_service_date || null,
      returned_date:             form.returned_date || null,
      returned_to_customer_date: form.returned_to_customer_date || null,
      taken_by_staff_id:         form.taken_by_staff_id || null,
      status:              form.status,
      notes:               form.notes.trim() || null,
    };
    try {
      if (editing) {
        const { data } = await servicePickupsApi.update(editing.id, payload);
        setRows(r => r.map(x => x.id === data.id ? data : x));
        show('Record updated!');
      } else {
        const { data } = await servicePickupsApi.create(payload);
        setRows(r => [data, ...r]);
        show('Record created!');
      }
      setModal(false);
    } catch(e) { setErr(parseErr(e, 'Failed to save.')); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await servicePickupsApi.delete(delItem.id);
      setRows(r => r.filter(x => x.id !== delItem.id));
      show('Deleted.');
    } catch { show('Delete failed.', 'error'); }
    finally { setDelItem(null); }
  };

  const filtered = rows.filter(r => {
    if (filter !== 'all' && r.status !== filter) return false;
    if (!q) return true;
    const ql = q.toLowerCase();
    return (r.customer_name || '').toLowerCase().includes(ql)
        || (r.customer_phone || '').includes(q)
        || (r.serial_number || '').toLowerCase().includes(ql);
  });

  const counts = {
    all:        rows.length,
    with_us:    rows.filter(r => r.status === 'with_us').length,
    at_service: rows.filter(r => r.status === 'at_service').length,
    returned:   rows.filter(r => r.status === 'returned').length,
  };

  if (busy) return <Spin/>;

  return (
    <div>
      <Flash/>
      <div className="page-hd">
        <h2>Service Pickups</h2>
        {canWrite && (
          <button className="btn btn-primary" onClick={openAdd}>
            <Plus s={14}/> Add Pickup
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 16 }}>
        {[
          { key: 'all',        label: 'Total',      color: 'var(--navy)'  },
          { key: 'with_us',    label: 'With Us',    color: '#1d4ed8'      },
          { key: 'at_service', label: 'At Service', color: '#b45309'      },
          { key: 'returned',   label: 'Returned',   color: 'var(--green)' },
        ].map(({ key, label, color }) => (
          <div key={key} className="stat-card" style={{ cursor: 'pointer', borderTop: filter === key ? `3px solid ${color}` : undefined }}
            onClick={() => setFilter(key)}>
            <div className="stat-lbl">{label}</div>
            <div className="stat-val" style={{ color }}>{counts[key]}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="search-row" style={{ marginBottom: 12 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by customer name, phone, or serial number…"/>
      </div>

      {/* Table */}
      <div className="card card-0">
        {filtered.length === 0
          ? <Empty msg="No service pickup records found."/>
          : (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Customer</th>
                    <th>Product</th>
                    <th>Serial No.</th>
                    <th>Taken From Customer</th>
                    <th>Sent to Service</th>
                    <th>Returned from Service</th>
                    <th>Returned to Customer</th>
                    <th>Taken By</th>
                    <th>Status</th>
                    {canWrite && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(row => {
                    const ss = STATUS_STYLE[row.status] || STATUS_STYLE.with_us;
                    return (
                      <tr key={row.id}>
                        <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--navy)', fontWeight: 600 }}>{row.pickup_code}</td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{row.customer_name}</div>
                          {row.customer_address && <div style={{ fontSize: 11, color: 'var(--text2)' }}>{row.customer_address}</div>}
                        </td>
                        <td style={{ fontWeight: 500 }}>{row.product_name}</td>
                        <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{row.serial_number || '—'}</td>
                        <td style={{ fontSize: 12 }}>{fmtDate(row.taken_date)}</td>
                        <td style={{ fontSize: 12 }}>{fmtDate(row.sent_to_service_date)}</td>
                        <td style={{ fontSize: 12 }}>{fmtDate(row.returned_date)}</td>
                        <td style={{ fontSize: 12 }}>{fmtDate(row.returned_to_customer_date)}</td>
                        <td style={{ fontSize: 12 }}>{row.taken_by_staff_name || '—'}</td>
                        <td>
                          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 20, background: ss.bg, color: ss.color }}>
                            {ss.label}
                          </span>
                        </td>
                        {canWrite && (
                          <td>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button className="btn btn-sm" onClick={() => openEdit(row)} title="Edit"><Edit s={12}/></button>
                              {canDelete && (
                                <button className="btn btn-sm btn-danger" onClick={() => setDelItem(row)} title="Delete"><Trash s={12}/></button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        }
      </div>

      {/* Add / Edit Modal */}
      <Modal
        open={showModal}
        onClose={() => setModal(false)}
        title={editing ? `Edit — ${editing.pickup_code}` : 'Add Service Pickup'}
        footer={<>
          <button className="btn" onClick={() => setModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Pickup'}
          </button>
        </>}
      >
        {err && <div className="notice n-err" style={{ marginBottom: 10 }}>{err}</div>}
        <div className="fg-2">
          <F label="Customer Name" req full>
            <input value={form.customer_name} onChange={set('customer_name')} placeholder="Customer full name"/>
          </F>
          <F label="Customer Address" full>
            <input value={form.customer_address} onChange={set('customer_address')} placeholder="Address / District"/>
          </F>
          <F label="Product / Inverter Name" req full>
            <input value={form.product_name} onChange={set('product_name')} placeholder="e.g. Luminous 3.5kVA Inverter"/>
          </F>
          <F label="Serial Number">
            <input value={form.serial_number} onChange={set('serial_number')} placeholder="e.g. LUM-2024-XXXXX"/>
          </F>

          <F label="Date Taken from Customer" req>
            <input type="date" value={form.taken_date} onChange={set('taken_date')}/>
          </F>
          <F label="Sent to Service Center">
            <input type="date" value={form.sent_to_service_date} onChange={setDate('sent_to_service_date')}/>
          </F>
          <F label="Returned from Service">
            <input type="date" value={form.returned_date} onChange={setDate('returned_date')}/>
          </F>
          <F label="Returned to Customer">
            <input type="date" value={form.returned_to_customer_date} onChange={setDate('returned_to_customer_date')}/>
          </F>

          <F label="Staff Who Took It">
            <select value={form.taken_by_staff_id} onChange={set('taken_by_staff_id')}>
              <option value="">— Select staff —</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
            </select>
          </F>

          <F label="Status">
            <select value={form.status} onChange={set('status')}>
              <option value="with_us">With Us</option>
              <option value="at_service">At Service Center</option>
              <option value="returned">Returned to Customer</option>
            </select>
          </F>

          <F label="Notes" full>
            <textarea value={form.notes} onChange={set('notes')} placeholder="Fault description, service notes…" style={{ minHeight: 60 }}/>
          </F>
        </div>
      </Modal>

      <Confirm
        open={!!delItem}
        onClose={() => setDelItem(null)}
        onOk={doDelete}
        msg={`Delete pickup record ${delItem?.pickup_code} for ${delItem?.customer_name}? This cannot be undone.`}
      />
    </div>
  );
}
